/** CDP postback config stored as JSON on routing_choice.cdp_postback_config. */

export const CDP_POSTBACK_EVENTS = [
  'SENT',
  'DELIVRD',
  'UNDELIV',
  'ENROUTE',
  'CLICK',
  'REJECTED',
  'UNDELIVERED',
  'DELIVERED',
  'EXPIRED',
] as const;

export type CdpPostbackEvent = (typeof CDP_POSTBACK_EVENTS)[number];

export const DEFAULT_FIELD_MAPPING: Record<string, string> = {
  goal: 'event',
  phone: 'to',
  sub_id10: 'correlation_id',
  sub_id9: 'system_id',
};

export interface CdpPostbackConfig {
  enabled: boolean;
  url: string;
  method: 'POST' | 'GET';
  events: string[];
  timeout_sec: number;
  max_retries: number;
  retry_delay_sec: number;
  field_mapping: Record<string, string>;
  static_fields: Record<string, string>;
}

export interface CdpFieldPair {
  key: string;
  value: string;
}

export function createDefaultCdpPostbackConfig(): CdpPostbackConfig {
  return {
    enabled: false,
    url: '',
    method: 'POST',
    events: [...CDP_POSTBACK_EVENTS],
    timeout_sec: 10,
    max_retries: 5,
    retry_delay_sec: 5,
    field_mapping: { ...DEFAULT_FIELD_MAPPING },
    static_fields: { project_id: '' },
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function asStringRecord(value: unknown): Record<string, string> {
  const src = asRecord(value);
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(src)) {
    if (v == null) continue;
    out[k] = String(v);
  }
  return out;
}

export function parseCdpPostbackConfig(
  value: Record<string, unknown> | null | undefined,
): CdpPostbackConfig {
  const base = createDefaultCdpPostbackConfig();
  if (!value || typeof value !== 'object') return base;

  const methodRaw = String(value.method ?? 'POST').toUpperCase();
  const method: 'POST' | 'GET' = methodRaw === 'GET' ? 'GET' : 'POST';

  const events = Array.isArray(value.events)
    ? value.events.map((e) => String(e)).filter(Boolean)
    : base.events;

  const fieldMapping = {
    ...DEFAULT_FIELD_MAPPING,
    ...asStringRecord(value.field_mapping),
  };
  const staticFields = {
    project_id: '',
    ...asStringRecord(value.static_fields),
  };

  return {
    enabled: Boolean(value.enabled),
    url: value.url != null ? String(value.url) : '',
    method,
    events,
    timeout_sec: Number(value.timeout_sec ?? base.timeout_sec) || base.timeout_sec,
    max_retries: Number(value.max_retries ?? base.max_retries) || base.max_retries,
    retry_delay_sec: Number(value.retry_delay_sec ?? base.retry_delay_sec) || base.retry_delay_sec,
    field_mapping: fieldMapping,
    static_fields: staticFields,
  };
}

export function serializeCdpPostbackConfig(config: CdpPostbackConfig): Record<string, unknown> {
  return {
    enabled: Boolean(config.enabled),
    url: config.url?.trim() || '',
    method: config.method === 'GET' ? 'GET' : 'POST',
    events: [...(config.events ?? [])],
    timeout_sec: Number(config.timeout_sec) || 0,
    max_retries: Number(config.max_retries) || 0,
    retry_delay_sec: Number(config.retry_delay_sec) || 0,
    field_mapping: { ...config.field_mapping },
    static_fields: { ...config.static_fields },
  };
}

export function pairsFromRecord(record: Record<string, string>): CdpFieldPair[] {
  const entries = Object.entries(record);
  if (!entries.length) return [{ key: '', value: '' }];
  return entries.map(([key, value]) => ({ key, value }));
}

export function recordFromPairs(pairs: CdpFieldPair[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const pair of pairs) {
    const key = pair.key?.trim();
    if (!key) continue;
    out[key] = pair.value ?? '';
  }
  return out;
}
